/**
 * Recurring Task Helper Functions
 * Manages the logic for recurring tasks
 */

/**
 * Calculate the next due date based on recurring type
 * @param {string} recurringType - Type of recurrence (daily, weekly, etc.)
 * @param {number} recurringInterval - Custom interval for 'custom' type
 * @param {string} lastCompletedDate - ISO string of last completion date
 * @returns {string} ISO string of next due date
 */
export function calculateNextDueDate(recurringType, recurringInterval = 1, lastCompletedDate = null) {
  const baseDate = lastCompletedDate ? new Date(lastCompletedDate) : new Date();
  const nextDate = new Date(baseDate);

  switch (recurringType) {
    case 'daily':
      nextDate.setDate(nextDate.getDate() + 1);
      break;
    
    case 'weekdays':
      // Move to next weekday (Mon-Fri)
      nextDate.setDate(nextDate.getDate() + 1);
      while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
        nextDate.setDate(nextDate.getDate() + 1);
      }
      break;
    
    case 'weekends':
      // Move to next weekend day (Sat-Sun)
      nextDate.setDate(nextDate.getDate() + 1);
      while (nextDate.getDay() !== 0 && nextDate.getDay() !== 6) {
        nextDate.setDate(nextDate.getDate() + 1);
      }
      break;
    
    case 'weekly':
      nextDate.setDate(nextDate.getDate() + 7);
      break;
    
    case 'biweekly':
      nextDate.setDate(nextDate.getDate() + 14);
      break;
    
    case 'monthly':
      nextDate.setDate(nextDate.getDate() + 30);
      break;
    
    case 'custom':
      nextDate.setDate(nextDate.getDate() + (recurringInterval || 1));
      break;
    
    default:
      nextDate.setDate(nextDate.getDate() + 1);
  }

  return nextDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
}

/**
 * Check if a recurring task should be recreated
 * @param {object} task - Task object
 * @returns {boolean} True if task should be recreated
 */
export function shouldRecreateTask(task) {
  if (!task.isRecurring) return false;
  if (task.status !== 'completed') return false;
  
  // Check if the task was completed and approved
  if (!task.approvedAt) return false;
  
  return true;
}

/**
 * Create a new recurring task instance from a completed one
 * @param {object} completedTask - The completed task
 * @returns {object} New task object
 */
export function createRecurringTaskInstance(completedTask) {
  const nextDueDate = calculateNextDueDate(
    completedTask.recurringType,
    completedTask.recurringInterval,
    completedTask.completedAt || new Date().toISOString()
  );

  return {
    title: completedTask.title,
    description: completedTask.description,
    assignedTo: completedTask.assignedTo,
    allowance: completedTask.allowance,
    dueDate: nextDueDate,
    photoRequired: completedTask.photoRequired,
    isRecurring: true,
    recurringType: completedTask.recurringType,
    recurringInterval: completedTask.recurringInterval,
    parentTaskId: completedTask.parentTaskId || completedTask.id, // Track original recurring task
    status: 'pending',
    createdAt: new Date().toISOString(),
    createdBy: completedTask.createdBy
  };
}

/**
 * Get a human-readable description of the recurring schedule
 * @param {string} recurringType - Type of recurrence
 * @param {number} recurringInterval - Custom interval
 * @returns {string} Human-readable description
 */
export function getRecurringDescription(recurringType, recurringInterval = 1) {
  const descriptions = {
    daily: 'Repeats daily',
    weekdays: 'Repeats on weekdays (Mon-Fri)',
    weekends: 'Repeats on weekends (Sat-Sun)',
    weekly: 'Repeats every 7 days',
    biweekly: 'Repeats every 14 days',
    monthly: 'Repeats every 30 days',
    custom: `Repeats every ${recurringInterval} day${recurringInterval > 1 ? 's' : ''}`
  };

  return descriptions[recurringType] || 'One-time task';
}

/**
 * Get an icon for the recurring type
 * @param {string} recurringType - Type of recurrence
 * @returns {string} Emoji icon
 */
export function getRecurringIcon(recurringType) {
  const icons = {
    daily: '⏰',
    weekdays: '📅',
    weekends: '🎉',
    weekly: '📆',
    biweekly: '📋',
    monthly: '🗓️',
    custom: '⚙️'
  };

  return icons[recurringType] || '🔄';
}













